import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { applyBrandColor } from "@/lib/color";

const DEFAULTS = {
  brand_name: "iplogin",
  brand_tagline: "Secure Access Portal",
  brand_subtitle: "Admin Dashboard",
  primary_color: "#206bc4",
  logo_url: "",
  // Iter 111 — Optional per-theme overrides. When set, BrandLogo uses
  // `logo_url_light` for light mode and `logo_url_dark` for dark mode
  // instead of `logo_url` for both. Empty string means "fall back to
  // logo_url, then to the shipped Conduit v2 default".
  logo_url_light: "",
  logo_url_dark: "",
  // Iter 115b — platform switches for the account-menu items.
  show_account_security: true,
  show_replay_tour: true,
};

const BrandingContext = createContext(null);

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULTS);
  // Iter 115b — platform-wide account-menu switches. Kept separate from
  // `brand` so a per-user `apply()` (partner branding) can't drop them.
  const [menu, setMenu] = useState({ show_account_security: true, show_replay_tour: true });

  const apply = useCallback((b) => {
    setBrand(b);
    applyBrandColor(b.primary_color);
    document.title = `${b.brand_name} ${b.brand_subtitle || ""}`.trim();
  }, []);

  const refresh = useCallback(async () => {
    // Iter 119ab — `?brand_preview=<base64 JSON>` renders the real login page
    // with UNSAVED branding so an admin can eyeball it in a new tab. Purely
    // visual and never persisted; ignored if the payload doesn't parse.
    try {
      const raw = new URLSearchParams(window.location.search).get("brand_preview");
      if (raw) {
        apply({ ...DEFAULTS, ...JSON.parse(decodeURIComponent(escape(atob(raw)))) });
        return;
      }
    } catch { /* fall through to the real branding */ }
    try {
      const { data } = await api.get("/branding");
      setMenu({
        show_account_security: data.show_account_security !== false,
        show_replay_tour: data.show_replay_tour !== false,
      });
      apply({ ...DEFAULTS, ...data });
    } catch {
      apply(DEFAULTS);
    }
  }, [apply]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <BrandingContext.Provider value={{ brand: { ...brand, ...menu }, refresh, apply }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext) || { brand: DEFAULTS, refresh: () => {}, apply: () => {} };
